import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { money, num } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ErrorState, Notice, PageHead } from '../../components/ui';

/* ==========================================================================
   فريق المبيعات — من يبيع، وبأيّ هدفٍ ونسبةٍ ورصيد
   ========================================================================== */

/**
 * الفريقُ يُبنى من الموارد البشرية لا هنا.
 *
 * الموظّفُ يُنشأ في ملفّه ويُوسم «مندوب مبيعات»، فيظهر في هذه القائمة
 * تلقائيًّا. ولا يُضاف أحدٌ من هنا: قائمتان لأسماء الناس تعنيان اسمًا في
 * إحداهما لا يعرفه الآخر.
 *
 * وظهورُه وحدَه لا يكفي — من لا هدفَ له ولا نسبةَ عمولة لا يدخل كشفًا ولا
 * يُقاس أداؤه، فيبقى اسمًا بلا أثر. فتُقال حالُه في صفّه، ويُفتح ملفُّه
 * بنقرة.
 *
 * والملفُّ صفحةٌ لا صفٌّ يتمدّد: المندوبُ سجلٌّ له تاريخٌ وأرقام، وفتحُه
 * تحت صفّه يطيل القائمةَ بما لا يخصّ قارئَها ويضيع ما بين الصفوف.
 */

interface Rep {
  employeeId: string;
  name: string;
  code: string | null;
  jobTitle: string | null;
  userId: string | null;
  commissionRate: number | null;
  discountBudget: number | null;
  discountBudgetOwn: number | null;
  yearTarget: number | null;
  quarters: Array<{ q: number; amount: number | null }>;
  months: Array<{ month: number; amount: number | null }>;
  missing: string[];
  ready: boolean;
}

interface TeamData {
  year: number;
  rows: Rep[];
  company: {
    yearTarget: number;
    assigned: number;
    gap: number;
    hintHeads: number;
    discountBudget: number;
    discountAssigned: number;
    discountOver: number;
  };
  needSetup: string[];
}

export function SalesTeam() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const navigate = useNavigate();

  const team = useQuery({
    queryKey: ['sales', 'team', year],
    queryFn: () => api<TeamData>(`/sales/targets/team${qs({ year })}`),
    placeholderData: (prev) => prev,
  });

  if (team.isError) {
    return (
      <div className="page">
        <ErrorState error={team.error} onRetry={() => void team.refetch()} />
      </div>
    );
  }

  const d = team.data;
  const c = d?.company;
  /*
   * الناقصون يُشتقّون من الصفوف لا من قائمة الأسماء.
   *
   * الخادمُ يرسل أسماءهم نصًّا، والاسمُ لا يُفتح به ملفّ. والصفوفُ فيها
   * المعرّفُ والحالةُ معًا، فمنها يُبنى ما يُنقر عليه.
   */
  const needSetup = (d?.rows ?? []).filter((r) => !r.ready);

  return (
    <div className="page">
      <PageHead
        title="فريق المبيعات"
        subtitle="من يبيع، وبأيّ هدفٍ ونسبةٍ ورصيد خصم"
        actions={
          <>
            <span className="seg" role="group" aria-label="السنة">
              <button
                type="button"
                className="seg-btn"
                onClick={() => setYear((y) => y - 1)}
                title="السنة السابقة"
              >
                <Icon name="chevronRight" size={13} />
              </button>
              <span className="seg-btn is-on num">{year}</span>
              <button
                type="button"
                className="seg-btn"
                onClick={() => setYear((y) => Math.min(now.getFullYear() + 1, y + 1))}
                disabled={year >= now.getFullYear() + 1}
                title="السنة التالية"
              >
                <Icon name="chevronLeft" size={13} />
              </button>
            </span>
            <Link to="/settings/commissions" className="btn">
              <Icon name="settings" size={15} />
              إعدادات المبيعات
            </Link>
          </>
        }
      />

      {/*
        ثلاثةُ تنبيهاتٍ لا زينة.

        الأوّل: مجموعُ أهداف الفريق دون هدف الشركة — والفجوةُ ليست رأيًا،
        إمّا أن تُرفع أهدافُ من عندك أو يُوظَّف من يحملها. والثاني: أرصدةُ
        الخصم وُزّع منها أكثرُ ممّا للشركة، فالسقفُ صار حبرًا. والثالث:
        أسماءٌ في القائمة بلا هدفٍ ولا نسبة — تُذكر بأسمائها روابطَ تُفتح،
        فالتنبيهُ الذي يقول «اضبطهم» ولا يدلّ على أين يترك قارئَه يبحث.
      */}
      {(needSetup.length > 0 ||
        (c != null && (c.gap > 0 || c.discountOver > 0))) && (
        <div className="notices">
          {c && c.gap > 0 && (
            <Notice
              kind="warning"
              icon="target"
              title="فجوةٌ في الهدف السنوي"
              action={
                <Link to="/settings/commissions" className="btn btn-sm">
                  الأهداف البيعية
                </Link>
              }
            >
              هدفُ الشركة <span className="num">{money(c.yearTarget)}</span>{' '}
              ووُزّع على الفريق{' '}
              <span className="num">{money(c.assigned)}</span> — الفارق{' '}
              <b className="num">{money(c.gap)}</b>.{' '}
              {c.hintHeads > 0
                ? `ارفع أهداف الفريق، أو عيّن نحو ${num(c.hintHeads)} مندوبًا بمتوسّط الفريق الحالي.`
                : 'وزّع الهدف على الفريق أو ارفع أهدافهم.'}
            </Notice>
          )}

          {c && c.discountOver > 0 && (
            <Notice
              kind="danger"
              icon="alert"
              title="أرصدةُ الخصم تتجاوز رصيد الشركة"
              action={
                <Link to="/settings/commissions" className="btn btn-sm">
                  رصيد الخصم
                </Link>
              }
            >
              وُزّع <span className="num">{money(c.discountAssigned)}</span>{' '}
              ورصيدُ الشركة{' '}
              <span className="num">{money(c.discountBudget)}</span> — الزيادة{' '}
              <b className="num">{money(c.discountOver)}</b>. اخفض أرصدة الأفراد
              أو ارفع رصيد الشركة.
            </Notice>
          )}

          {needSetup.length > 0 && (
            <Notice
              kind="info"
              icon="user"
              title={`${num(needSetup.length)} ${needSetup.length === 1 ? 'مندوبٌ ينقصه الضبط' : 'من المندوبين ينقصهم الضبط'}`}
            >
              من لا هدفَ له ولا نسبةَ عمولة لا يدخل كشفًا ولا يُقاس أداؤه. افتح
              ملفَّه واضبطهما.
              <span className="notice-names">
                {needSetup.map((r) => (
                  <Link
                    key={r.employeeId}
                    to={`/sales/team/${r.employeeId}`}
                    className="notice-name"
                  >
                    {r.name}
                    <Icon name="chevronLeft" size={12} />
                  </Link>
                ))}
              </span>
            </Notice>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">المندوبون</h2>
          <span className="tiny mute">
            يُضافون من ملفّاتهم في الموارد البشرية بوسم «مندوب مبيعات»
          </span>
        </div>

        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>المندوب</th>
                <th className="col-num">هدف {year}</th>
                <th className="col-num">نسبة العمولة</th>
                <th className="col-num">رصيد الخصم</th>
                <th>الحالة</th>
                <th className="col-act" />
              </tr>
            </thead>
            <tbody>
              {(d?.rows ?? []).map((r) => (
                <tr
                  key={r.employeeId}
                  className="clickable"
                  onClick={() => navigate(`/sales/team/${r.employeeId}`)}
                >
                  <td>
                    <div className="cell-strong">{r.name}</div>
                    <div className="tiny mute">
                      {r.jobTitle ?? '—'}
                      {r.code ? ` · ${r.code}` : ''}
                    </div>
                  </td>
                  <td className="num">
                    {r.yearTarget ? money(r.yearTarget) : '—'}
                  </td>
                  <td className="num">
                    {r.commissionRate ? `${r.commissionRate}٪` : '—'}
                  </td>
                  <td className="num">
                    {r.discountBudget ? money(r.discountBudget) : '—'}
                  </td>
                  <td>
                    {r.ready ? (
                      <span className="badge badge-success">مضبوط</span>
                    ) : (
                      <span className="badge badge-warning">
                        ينقصه {r.missing.join(' و')}
                      </span>
                    )}
                  </td>
                  <td className="col-act">
                    <Icon name="chevronLeft" size={14} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {d?.rows.length === 0 && (
          <p className="tiny mute center" style={{ padding: 'var(--sp-4)' }}>
            لا مندوبين بعد. افتح ملفَّ الموظّف في الموارد البشرية وضع علامة
            «مندوب مبيعات».
          </p>
        )}
      </div>

    </div>
  );
}
